import type { FC, ReactNode } from 'react'
import type { PostSummary } from '@/types'

import Link from 'next/link'
import { BrandMark } from '@/components/generals/brand-mark'
import { PostMeta } from '@/components/journal/post-meta'

interface PostCardProps {
    post: PostSummary
    priority?: boolean
    headingLevel?: 'h2' | 'h3'
}

export const PostCard: FC<PostCardProps> = ({
    post,
    priority = false,
    headingLevel = 'h3'
}): ReactNode => {
    const Heading = headingLevel
    const href = `/journal/${post.slug}`

    return (
        <article className='group flex flex-col'>
            <Link
                href={href}
                tabIndex={-1}
                aria-hidden='true'
                className='relative block aspect-[4/3] overflow-hidden rounded-xl bg-muted'
            >
                {post.cover ? (
                    <img
                        src={post.cover}
                        alt={post.cover_alt ?? ''}
                        loading={priority ? 'eager' : 'lazy'}
                        decoding='async'
                        className='size-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04] motion-reduce:transition-none'
                    />
                ) : (
                    <span className='flex size-full items-center justify-center text-primary/35'>
                        <BrandMark className='size-16' />
                    </span>
                )}
            </Link>

            <div className='mt-5 flex flex-1 flex-col'>
                {post.category_id && post.category_name && post.category_slug && (
                    <Link
                        href={`/journal/category/${post.category_slug}`}
                        className='self-start text-xs font-medium uppercase tracking-[0.18em] text-primary transition-colors hover:text-foreground'
                    >
                        <span data-mdlfy-cms={`categories:${post.category_id}:name`}>{post.category_name}</span>
                    </Link>
                )}

                <Heading className='mt-3 font-heading text-2xl leading-snug tracking-tight'>
                    <Link
                        href={href}
                        className='decoration-foreground/30 decoration-1 underline-offset-[6px] transition-colors group-hover:underline'
                    >
                        <span data-mdlfy-cms={`posts:${post.id}:title`}>{post.title}</span>
                    </Link>
                </Heading>

                {post.excerpt && (
                    <p
                        data-mdlfy-cms={`posts:${post.id}:excerpt`}
                        className='mt-3 line-clamp-3 leading-relaxed text-muted-foreground'
                    >
                        {post.excerpt}
                    </p>
                )}

                <PostMeta
                    className='mt-4'
                    postId={post.id}
                    publishedAt={post.published_at}
                    readMinutes={post.read_minutes}
                />
            </div>
        </article>
    )
}
